// Supplies the Cognito ID token that the shared BASE gateway's authorizer now
// requires on every dashboard-facing route. Reads the same localStorage
// session AuthContext writes ('fce.auth'), refreshes it via the Cognito
// REFRESH_TOKEN_AUTH flow when the token is near expiry (or after the gateway
// rejects one), and single-flights concurrent refreshes so the dashboard's
// first-paint burst of context loads triggers at most one Cognito call.

import { cognitoApi, decodeIdToken } from './cognitoApi';

const STORAGE_KEY = 'fce.auth';

// Refresh when less than this many seconds of validity remain, so a token
// can't expire between leaving the browser and reaching the authorizer.
const EXPIRY_SLACK_SECONDS = 120;

function readStored() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.idToken) return null;
        return parsed;
    } catch {
        return null;
    }
}

function writeStored(value) {
    if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else       localStorage.removeItem(STORAGE_KEY);
}

let refreshInFlight = null;

// One refresh at a time; every concurrent caller awaits the same promise.
// REFRESH_TOKEN_AUTH doesn't rotate the refresh token, so only the id/access
// tokens are replaced in storage.
function refreshSession(stored) {
    if (!refreshInFlight) {
        refreshInFlight = (async () => {
            const res  = await cognitoApi.refresh(stored.refreshToken);
            const auth = res.AuthenticationResult || {};
            if (!auth.IdToken) {
                const err = new Error('Empty refresh response');
                err.code = 'NotAuthorizedException';
                throw err;
            }
            const next = {
                ...(readStored() || stored),
                idToken:     auth.IdToken,
                accessToken: auth.AccessToken || stored.accessToken,
            };
            writeStored(next);
            return auth.IdToken;
        })().finally(() => { refreshInFlight = null; });
    }
    return refreshInFlight;
}

// The session is unrecoverable (refresh token expired or revoked): clear it
// and reload into the login page so AuthContext re-initialises from empty
// storage. Guarded so a burst of failing calls only navigates once.
let loggingOut = false;
export function hardLogout() {
    writeStored(null);
    if (!loggingOut && !window.location.pathname.startsWith('/login')) {
        loggingOut = true;
        window.location.assign('/login');
    }
}

// Returns an ID token for the BASE gateway, refreshing first when it's near
// expiry. Returns null when there is no session at all, the caller sends no
// header and the gateway's 401 is the honest answer.
export async function getIdToken() {
    const stored = readStored();
    if (!stored?.idToken) return null;

    const claims      = decodeIdToken(stored.idToken);
    const secondsLeft = (claims?.exp || 0) - Math.floor(Date.now() / 1000);
    if (secondsLeft > EXPIRY_SLACK_SECONDS) return stored.idToken;
    if (!stored.refreshToken) return stored.idToken;

    try {
        return await refreshSession(stored);
    } catch (err) {
        if (String(err.code || '').includes('NotAuthorized')) {
            // Refresh token dead, sign out rather than let every call 401.
            hardLogout();
            return null;
        }
        // Transient (network) failure: fall back to the stored token and let
        // the request itself succeed or 401.
        return stored.idToken;
    }
}

// A 401 from the gateway means the token it saw was expired or invalid
// regardless of what the local clock thinks (covers skew and revocation).
// Returns the fresh token, or null when the session can't be refreshed.
export async function refreshIdTokenAfter401() {
    const stored = readStored();
    if (!stored?.refreshToken) return null;
    try {
        return await refreshSession(stored);
    } catch {
        return null;
    }
}
